import React, { useContext, useEffect, useState, useRef } from "react";
import { useSearchParams } from "react-router";
import { useMutation } from "@tanstack/react-query";

import { getCustomer, refetchCustomer } from "../queries/global";
import {
  NotificationContext,
  handleError,
} from "../context/NotificationContext";

import * as QUERIES from "../queries/query-functions";

import LoadingIndicator from "../components/loading-indicator";
import AvatarCircle from "../svg/avatar-circle";

export default function Login() {
  const { notification, setNotification } = useContext(NotificationContext);
  const [email, setEmail] = useState(null);
  const [password, setPassword] = useState(null);
  const [emailAlert, setEmailAlert] = useState(null);
  const [passwordAlert, setPasswordAlert] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const emailInputRef = useRef(null);

  const {
    data: customer,
    isPending: customerPending,
    isFetching: customerFetching,
  } = getCustomer();
  const [searchParams, setSearchParams] = useSearchParams();

  const redirect_param = searchParams.get("r");
  // Only redirect within the site. Relative values (e.g. "digital-library") are made root relative
  // so they don't resolve against the current page.
  const from =
    redirect_param && /^\/?[^/\\]/.test(redirect_param)
      ? "/" + redirect_param.replace(/^\//, "")
      : "/account";

  const logIn = useMutation({
    mutationFn: () => QUERIES.logIn({ email, password }),
    onError: (error) => {
      handleError(error.message, setNotification);
    },
    onSuccess: (data) => {
      console.log("refetchCustomer", data);

      refetchCustomer();
    },
  });

  const resetPassword = useMutation({
    mutationFn: () => QUERIES.resetPassword(email),
    onError: (error) => {
      handleError(error.message, setNotification);
    },
    onSuccess: () => {
      setSuccessMessage("A reset link has been sent to your email address.");
    },
  });

  const handleLogin = () => {
    let has_alert = false;
    if (!email || !emailInputRef.current.checkValidity()) {
      has_alert = true;
      setEmailAlert("Please provide a valid email address.");
    }
    if (!password) {
      has_alert = true;
      setPasswordAlert("Please provide a password.");
    }
    if (has_alert) return;
    logIn.mutateAsync();
  };

  const handleResetPassword = () => {
    if (!email || !emailInputRef.current.checkValidity()) {
      setEmailAlert("Please provide a valid email address.");
      return;
    }
    resetPassword.mutateAsync();
  };

  useEffect(() => {
    // Wait for fresh customer data - cached data can belong to a customer who just logged out
    if (!customer || logIn.isPending || customerFetching) return;

    // If the user is logged in redirect them. Sync the PHP session first, otherwise
    // session gated pages (digital library) send them straight back here.
    QUERIES.setWordpressSession(customer.email)
      .catch(() => {})
      .finally(() => {
        window.location = from;
      });
  }, [customer, logIn.isPending, customerFetching]);

  if (customerPending || customerFetching || customer)
    return <LoadingIndicator type="page" />;

  return (
    <div className="booking-pathway__container booking-pathway__login">
      <div className="booking-pathway__main-content">
        <h1 className="booking-pathway__header">
          <AvatarCircle /> Login
        </h1>

        <form className="booking-pathway__login--form login-form">
          <div className="form-field form-field__email">
            <label htmlFor="spektrixLoginEmail">
              Email address
              <span className="label__required">(required)</span>
            </label>
            <input
              type="email"
              ref={emailInputRef}
              id="spektrixLoginEmail"
              autoComplete="email"
              onChange={(e) => {
                setEmailAlert(null);
                setEmail(e.target.value);
              }}
              className={emailAlert ? "form-field--error" : ""}
              required
            />
          </div>

          <div className="form-field form-field__password">
            <label htmlFor="spektrixLoginPassword">
              Password
              <span className="label__required">(required)</span>
            </label>
            <input
              type="password"
              id="spektrixLoginPassword"
              autoComplete="current-password"
              onChange={(e) => setPassword(e.target.value)}
              className={passwordAlert ? "form-field--error" : ""}
              required
            />
          </div>

          <p className="login-form__forgot-password">
            Forgotten password?
            <button
              type="button"
              className="login-form__forgot-password--link"
              onClick={() => handleResetPassword()}
            >
              Reset password
            </button>
          </p>

          <div role="alert" className="alert-messages">
            {successMessage && (
              <p className="confirmation-message">{successMessage}</p>
            )}
            {emailAlert && <p className="error-message">{emailAlert}</p>}
            {passwordAlert && <p className="error-message">{passwordAlert}</p>}
          </div>

          <div className="login-form__footer">
            <button
              type="button"
              className="btn btn--primary"
              onClick={() => handleLogin()}
            >
              Login now
            </button>
            <a href="/create-account" className="btn">
              Create account
            </a>
          </div>
        </form>
      </div>
    </div>
  );
}
